import { Box, Column, Input, Row, Text } from '@amzn/meridian';
import type { FormContextType, RJSFSchema, StrictRJSFSchema, WidgetProps } from '@rjsf/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRequiredFieldOnChange } from '../utils/useRequiredFieldOnChange';

const pickerButtonDimension = '40px';

/**
 * Normalizes hex color codes to full 6-character format.
 * Handles both shorthand (#000) and full format (#000000).
 * Adds # prefix if missing.
 */
function normalizeHex(input: string): string {
  if (!input) return '';

  let hex = input.trim().toUpperCase();

  if (!hex.startsWith('#')) {
    hex = `#${hex}`;
  }

  if (hex.length === 4) {
    return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  }

  return hex;
}

function validateHex(hex: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(hex);
}

/**
 * Custom RJSF widget for color selection with text input and native color picker.
 *
 * Provides dual input methods:
 * - Text input for typing hex codes (supports shorthand #000 and full #000000)
 * - Color picker button for visual selection using browser's native picker
 *
 * Automatically normalizes shorthand hex codes to full 6-character format.
 *
 * @example
 * // In JSON Schema:
 * {
 *   "backgroundColor": {
 *     "type": "string",
 *     "title": "Background color"
 *   }
 * }
 *
 * // In UI Schema:
 * {
 *   "backgroundColor": {
 *     "ui:widget": "colorPicker"
 *   }
 * }
 */
export const ColorPicker = <T = never, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = never>(
  props: WidgetProps<T, S, F>,
) => {
  const { onChange, value, id, disabled, label, required } = props;

  const requiredFieldOnChange = useRequiredFieldOnChange(onChange, required);
  const [inputValue, setInputValue] = useState<string>(() => normalizeHex(value as string));

  const normalizedValue = useMemo(
    () => normalizeHex(value as string),
    [
      value,
    ],
  );

  const isValidHex = useMemo(
    () => validateHex(inputValue),
    [
      inputValue,
    ],
  );

  useEffect(() => {
    setInputValue(normalizedValue);
  }, [
    normalizedValue,
  ]);

  const handleTextChange = useCallback((value: string) => {
    setInputValue(value);
  }, []);

  const handleTextBlur = useCallback(() => {
    const normalized = normalizeHex(inputValue);
    if (validateHex(normalized)) {
      setInputValue(normalized);
      requiredFieldOnChange(normalized);
    } else {
      requiredFieldOnChange(inputValue);
    }
  }, [
    inputValue,
    requiredFieldOnChange,
  ]);

  const handleColorChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setInputValue(newValue);
      requiredFieldOnChange(newValue);
    },
    [
      requiredFieldOnChange,
    ],
  );

  const displayError = useMemo(() => {
    if (!inputValue && required) {
      return 'Required';
    }

    if (!isValidHex && inputValue) {
      return 'Invalid hex color format. Please enter short or full hex code (e.g. #FFF or #05A0D1).';
    }
  }, [
    inputValue,
    required,
    isValidHex,
  ]);

  return (
    <Box maxWidth="300px">
      <Text htmlFor={id} id={`${id}-label`} tag="label">
        {label}
      </Text>
      <Row
        alignmentVertical="bottom"
        widths={[
          'fill',
          'fit',
        ]}
      >
        <Column>
          <Input
            disabled={disabled}
            error={!!displayError}
            id={id}
            onBlur={handleTextBlur}
            onChange={handleTextChange}
            value={inputValue}
          />
        </Column>
        <Column>
          <input
            aria-label="Color picker"
            disabled={disabled}
            onChange={handleColorChange}
            style={{
              height: pickerButtonDimension,
              width: pickerButtonDimension,
            }}
            type="color"
            value={normalizedValue}
          />
        </Column>
      </Row>
      {displayError && (
        <Box aria-live="polite" spacingInset="200 none none none">
          <Text color="error" type="b100">
            {displayError}
          </Text>
        </Box>
      )}
    </Box>
  );
};
